import * as React from 'react'
import classNames from 'classnames'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { choiceToggle } from '../flow/choice'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { TimelineDetails } from './TimelineDetails'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import { Checkbox } from '../../../shared/webcomponents/react-wrappers/Checkbox'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    maxChoices: number
    choices: Flows.Choice[]
    answerValue: Flows.CheckboxAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.CheckboxAnswer>
    openOptions?: () => void
}

export function ElementCheckbox(props: Props) {
    const handleClick = (id: Flows.ChoiceId) => {
        const { createAnswer, maxChoices, choices } = props
        const selection = props.answerValue ?? []

        // If max choices is 1 we replace the selected option
        if (maxChoices === 1) {
            return createAnswer((base) => ({
                ...base,
                type: 'checkbox',
                value: selection.includes(id) ? [] : [id],
            }))
        }

        const newSelection = choiceToggle(choices, selection, id)

        // If already made the maximum amount of choices, ignore
        if (maxChoices != null && maxChoices < newSelection.length) {
            return
        }

        createAnswer((base) => ({
            ...base,
            type: 'checkbox',
            value: newSelection,
        }))
    }

    const tryRender = () => {
        const { deps, elementId, required, label, timelineDetails, validationMessages, openOptions } = props

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={openOptions}
                />
                <div className="AppCore__flow__ElementCheckbox">
                    {props.choices.map((choice) => {
                        const selection = props.answerValue ?? []
                        const selected = selection.includes(choice.id)

                        return (
                            <div
                                key={choice.id}
                                className={classNames('AppCore__flow__ElementCheckbox__choice', {
                                    selected,
                                    'AppCore__flow__ElementCheckbox__choice--error': validationMessages.length !== 0,
                                })}
                                onClick={() => handleClick(choice.id)}
                            >
                                <Checkbox selected={selected} label={choice.label} />
                            </div>
                        )
                    })}
                </div>
            </ExecutionElement>
        )
    }
    return renderCatch(props.deps, tryRender)
}
